import React, { useState, useRef, useEffect } from "react";
import { graphql } from "gatsby";
import Swiper from "react-id-swiper";
import ArrowPrev from "../../images/circle-arrow-prev.svg";
import ArrowNext from "../../images/circle-arrow-next.svg";
import ArrowHoverSvg from "../../images/arrow-hover.svg";
import PrismicLink from "../prismicLink";
import { motion } from "framer-motion";
import { STAGGER, FADE_UP, useInView, combine } from "../animationUtils";
import { useWindowSize } from "react-use";

import { GatsbyImage } from "gatsby-plugin-image";

export const fragments = graphql`
  fragment Project_SliderImage on PrismicProjectDataBodyProjectsSlider {
    id
    slice_type
    primary {
      projects_slider_tags {
        text
      }
      projects_slider_title {
        text
      }
      projects_slider_cta {
        text
      }
      projects_slider_cta_link {
        uid
        url
      }
    }
    items {
      post_item {
        uid
        type
        document {
          ... on PrismicMediumPost {
            id
            data {
              title {
                text
              }
              featured_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
            }
            url
            tags
          }
          ... on PrismicProject {
            id
            data {
              title {
                text
              }
              client {
                text
              }
              featured_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
              carousel_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
            }
            url
            tags
          }
        }
      }
    }
  }
  fragment Page_SliderImage on PrismicPageDataBodyProjectsSlider {
    id
    slice_type
    primary {
      projects_slider_tags {
        text
      }
      projects_slider_title {
        text
      }
      projects_slider_cta {
        text
      }
      projects_slider_cta_link {
        uid
        url
      }
    }
    items {
      post_item {
        uid
        type
        document {
          ... on PrismicMediumPost {
            id
            data {
              title {
                text
              }
              featured_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
            }
            url
            tags
          }
          ... on PrismicProject {
            id
            data {
              title {
                text
              }
              client {
                text
              }
              featured_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
              carousel_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
            }
            url
            tags
          }
        }
      }
    }
  }
  fragment Homepage_ProjectsSlider on PrismicHomepageDataBodyProjectsSlider {
    id
    slice_type
    primary {
      projects_slider_tags {
        text
      }
      projects_slider_title {
        text
      }
      projects_slider_cta {
        text
      }
      projects_slider_cta_link {
        uid
        url
      }
    }
    items {
      post_item {
        uid
        type
        document {
          ... on PrismicMediumPost {
            id
            data {
              title {
                text
              }
              featured_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
            }
            url
            tags
          }
          ... on PrismicProject {
            id
            data {
              title {
                text
              }
              client {
                text
              }
              featured_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
              carousel_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
            }
            url
            tags
          }
        }
      }
    }
  }
  fragment FocusArea_ProjectsSlider on PrismicFocusAreaDataBodyProjectsSlider {
    id
    slice_type
    primary {
      projects_slider_tags {
        text
      }
      projects_slider_title {
        text
      }
      projects_slider_cta {
        text
      }
      projects_slider_cta_link {
        uid
        url
      }
    }
    items {
      post_item {
        uid
        type
        document {
          ... on PrismicMediumPost {
            id
            data {
              title {
                text
              }
              featured_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
            }
            url
            tags
          }
          ... on PrismicProject {
            id
            data {
              title {
                text
              }
              client {
                text
              }
              featured_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
              carousel_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 600)
              }
            }
            url
            tags
          }
        }
      }
    }
  }
`;

function SliderImages({ data, projects }) {
  const [ref, inView] = useInView();

  const site = useWindowSize();

  const [windowWidth, setWindowWidth] = useState(site.width);
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(site.width);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  });

  const params = {
    slidesPerView: 1,
    loop: true,
    speed: 1000,
    spaceBetween: 15,
    centeredSlides: false,
    watchOverflow: true,
    pagination: {
      clickable: true,
    },
    breakpoints: {
      992: {
        slidesPerView: 2.5,
        loopedSlides: 2,
        spaceBetween: 15,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 15,
      },
    },
  };

  const refSwiper = useRef(null);

  const setSwiper = useState(null);

  const nextHandler = () => {
    if (refSwiper.current !== null && refSwiper.current.swiper !== null) {
      refSwiper.current.swiper.slideNext();
    }
  };

  const prevHandler = () => {
    if (refSwiper.current !== null && refSwiper.current.swiper !== null) {
      refSwiper.current.swiper.slidePrev();
    }
  };

  const slidesToRender = projects?.length > 1 ? projects : data?.items;

  return (
    <motion.div
      ref={ref}
      {...combine(STAGGER, FADE_UP)}
      {...(inView ? { animate: "start" } : {})}
      className="slider-image"
    >
      <motion.div className="slider-header">
        <motion.h2 {...FADE_UP} className="h2 slider-image__title">
          {data?.primary?.projects_slider_title?.text}
        </motion.h2>
        {data?.primary?.projects_slider_cta_link && (
          <PrismicLink
            link={data?.primary?.projects_slider_cta_link}
            className="slider-image__cta"
          >
            {data?.primary?.projects_slider_cta?.text}
            <ArrowHoverSvg class="slider-image__arrow" />
          </PrismicLink>
        )}
      </motion.div>

      <motion.div {...FADE_UP} className="slider-image__box">
        <Swiper
          ref={refSwiper}
          {...params}
          getSwiper={setSwiper}
          activeSlideKey={1}
        >
          {slidesToRender?.map((item, idx) => {
            const isNode = item?.node?.type !== "project" ? true : false;
            const carouselImage = item?.post_item ?  item?.post_item.document?.data?.carousel_image : item?.node?.data?.carousel_image

            const img = !isNode && !carouselImage
              ? item?.node?.data?.featured_image
              : carouselImage?.gatsbyImageData
                ? carouselImage
                : item?.node?.data?.featured_image_square;

            const title = isNode
              ? item?.post_item?.document?.data?.title?.text
              : item?.node?.data?.title?.text;

            const clientOrTags = isNode
              ? item?.post_item?.type === "project"
                ? item?.post_item?.document?.data?.client?.text
                : item?.post_item?.document?.tags
              : item?.node?.data?.client?.text;

            const link = isNode ? item?.post_item : item?.node;

            return (
              <div className="col" key={idx}>
                <PrismicLink link={link} className="slider-image__item">
                  <figure>
                    {img?.gatsbyImageData && (
                      <GatsbyImage
                        image={img.gatsbyImageData}
                        alt={img?.alt || title || "Slide Image"}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                        imgStyle={{
                          objectFit: "cover",
                          objectPosition: "center center",
                        }}
                      />
                    )}
                  </figure>
                  <div className="slider-image__info">
                    <h3 className="h6">{title}</h3>
                    <p>{clientOrTags}</p>
                  </div>
                </PrismicLink>
              </div>
            );
          })}
        </Swiper>
        <div
          className={`slider-image__button ${projects.length <= 2 && windowWidth > 991 ? "slider-image__button-hidden" : ""}`}
        >
          <motion.button
            aria-label="Previous Slide"
            {...FADE_UP}
            className="prev"
            onClick={prevHandler}
          >
            <ArrowPrev className="circle-arrow" />
          </motion.button>
          <motion.button
            aria-label="Next Slide"
            {...FADE_UP}
            className="next"
            onClick={nextHandler}
          >
            <ArrowNext className="circle-arrow" />
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default SliderImages;
